import { Divider } from '@mui/material';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import { LoadingAnimation } from 'components';
import PropertiesLoadingAnimation from 'components/Loading/PropertiesLoadingAnimation';
import { PropertyPagination } from 'components/Pagination';
import { OverviewPropertyTable } from 'components/PropertyTable';
import useCollectionStore from 'store/useCollection';
import { useFiltersStore } from 'store/useFiltersStore';
import usePropertiesStore from 'store/usePropertiesStore';
import useUserStore from 'store/useUserStore';
import { PropertyFilterType, PropertySort } from 'types/PropertyFilters';
import { FilterType } from 'util/Types';
import { formatNumber } from 'util/Funcs';
import AISearchPanel from './AISearch';
import DrawMapPanel from './Sections/DrawMapPanel';
import ExportPanel from './Sections/ExportPanel';
import FilterPanel from './Sections/FilterPanel';
import PublicFilterPanel from './Sections/PublicFilterPanel';
import PublicTextSearch from './Sections/PublicTextSearch';
import InfoPanel from './Sections/InfoPanel';
import SavedFilterPanel from './Sections/SavedFilterPanel';
import PublicOverviewInfo from './Sections/PublicOverviewInfo';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

const convertToPropertyFilterType = (filter: FilterType): PropertyFilterType => {
    return {
        country: filter.country || '',
        city: filter.city || [],
        state: filter.state || [],
        type: filter.type || [],
        locations: filter.locations || [],
        price: {
            from: filter.price?.from || 0,
            to: filter.price?.to || 0,
        },
        constructionArea: {
            from: filter.constructionArea?.from || 0,
            to: filter.constructionArea?.to || 0,
        },
        plotArea: {
            from: filter.plotArea?.from || 0,
            to: filter.plotArea?.to || 0,
        },
        pricePerSquareMeter: {
            from: filter.pricePerSquareMeter?.from || 0,
            to: filter.pricePerSquareMeter?.to || 0,
        },
        bedbath: filter.bedbath || { bedIdx: [0], bathIdx: [0] },
        listingType: filter.listingType || '',
        neighborhood: filter.neighborhood || [],
        query: filter.query || '',
        availability: filter.availability || ['available'],
    };
};

const OverViewContent = () => {
    const { userInfo, isOnboardingUser } = useUserStore();
    const { currentFilter, savedFilterList } = useFiltersStore();
    const {
        currentPage,
        setCurrentPage,
        totalPageCount: storeTotalPageCount,
        fetchCompetitorAllProperties,
        fetchPublicProperties,
        fetchCompetitorAllPropertiesSort,
        fetchCompetitorAllPropertiesSetSort,
    } = usePropertiesStore();
    const { loadCollections } = useCollectionStore();

    const isLoggedIn = !!userInfo;
    const hasFinishedOnboarding = isLoggedIn && !isOnboardingUser();

    // For authenticated and onboarded users - use the full competitor properties endpoint
    const {
        isFetching: isFetchingAuth,
        data: dataAuth,
        isPlaceholderData: isPlaceholderDataAuth,
    } = useQuery({
        queryKey: ['fetchCompetitorAllProperties', currentPage, currentFilter, fetchCompetitorAllPropertiesSort],
        queryFn: () => fetchCompetitorAllProperties(convertToPropertyFilterType(currentFilter), fetchCompetitorAllPropertiesSort),
        staleTime: 0, // Always consider data stale to trigger refetch
        refetchOnWindowFocus: false,
        placeholderData: undefined, // Don't show placeholder data to ensure loading state is visible
        enabled: hasFinishedOnboarding && !!currentFilter,
    });

    // For non-logged-in users - use the public properties endpoint
    const {
        isFetching: isFetchingPublic,
        data: dataPublic,
        isPlaceholderData: isPlaceholderDataPublic,
    } = useQuery({
        queryKey: ['fetchPublicProperties', currentPage, currentFilter, fetchCompetitorAllPropertiesSort],
        queryFn: () => fetchPublicProperties(convertToPropertyFilterType(currentFilter), fetchCompetitorAllPropertiesSort),
        staleTime: 0, // Always consider data stale to trigger refetch
        refetchOnWindowFocus: false,
        placeholderData: undefined, // Don't show placeholder data to ensure loading state is visible
        enabled: !isLoggedIn && !!currentFilter,
    });

    // Use the appropriate data based on user status
    const isFetching = hasFinishedOnboarding ? isFetchingAuth : isFetchingPublic;
    const data = hasFinishedOnboarding ? dataAuth : dataPublic;
    const isPlaceholderData = hasFinishedOnboarding ? isPlaceholderDataAuth : isPlaceholderDataPublic;

    useQuery({
        queryKey: ['loadCollections'],
        queryFn: loadCollections,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        enabled: hasFinishedOnboarding,
    });

    const isFetchingAIProperties = Boolean(useIsMutating({ mutationKey: ['fetchAiProperties'] }));

    const [showFilter, setShowFilter] = useState(true);
    const [hasScrolled, setHasScrolled] = useState(false);
    const lastScrollY = useRef(0);
    const AUTH_THRESHOLD = 800;
    const PUBLIC_THRESHOLD = 1200;

    // Improved scroll handler with immediate hiding
    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    const currY = window.scrollY;
                    const threshold = isLoggedIn ? AUTH_THRESHOLD : PUBLIC_THRESHOLD;

                    if (currY < threshold) {
                        // Always show filters when near the top
                        setShowFilter(true);
                        // Reset hasScrolled when we're back at the top
                        if (hasScrolled) {
                            setHasScrolled(false);
                        }
                    } else {
                        if (currY > lastScrollY.current) {
                            // Scrolling down - hide filters immediately
                            setShowFilter(false);
                        } else {
                            // Scrolling up - immediately show filters
                            setShowFilter(true);
                            // Mark as scrolled when filters reappear
                            if (!hasScrolled) {
                                setHasScrolled(true);
                            }
                        }
                    }

                    lastScrollY.current = currY;
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isLoggedIn, hasScrolled]);

    const handleSort = useCallback((sort: PropertySort) => fetchCompetitorAllPropertiesSetSort(sort), [fetchCompetitorAllPropertiesSetSort]);

    const handlePageChange = useCallback(
        (page: number) => {
            setCurrentPage(page);
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        },
        [setCurrentPage],
    );

    const properties = useMemo(() => data?.findAllPropertiesByCompanyIds?.properties || [], [data?.findAllPropertiesByCompanyIds?.properties]);
    const total = useMemo(() => data?.findAllPropertiesByCompanyIds?.total || 0, [data?.findAllPropertiesByCompanyIds?.total]);
    const completeTotal = useMemo(() => data?.findAllPropertiesByCompanyIds?.completeTotal || 0, [data?.findAllPropertiesByCompanyIds?.completeTotal]);
    const totalPageCount = storeTotalPageCount;
    const showLoading = useMemo(() => {
        // Only include AI properties loading for authenticated users
        const aiLoading = hasFinishedOnboarding ? isFetchingAIProperties : false;
        // Show loading when fetching, even if we have placeholder data
        return isFetching || aiLoading;
    }, [isFetching, isFetchingAIProperties, hasFinishedOnboarding]);

    return (
        <div className="flex flex-col gap-[20px] py-[15px] lg:px-0 px-[20px]" style={{ willChange: 'scroll-position' }}>
            {/* Show promotional info for non-logged-in users */}
            {!isLoggedIn && <PublicOverviewInfo />}

            {/* Show info panel for authenticated users */}
            {hasFinishedOnboarding && <InfoPanel />}
            {/* <DrawMapPanel /> */}
            <div className="rounded-[15px]">
                {/* Show filter panel for authenticated and onboarded users */}
                {hasFinishedOnboarding && (
                    <>
                        <div
                            className={`
                                sticky top-0 bg-white z-20
                                rounded-t-[10px] lg:rounded-b-none ${hasScrolled ? 'rounded-b-[10px]' : 'rounded-b-none'}
                                transition-opacity duration-200
                                ${showFilter ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                            `}
                            style={{
                                willChange: 'opacity',
                                transform: 'translateZ(0)', // Hardware acceleration
                            }}
                        >
                            <div className={`px-[16px] py-[20px] ${showLoading ? 'pointer-events-none opacity-50' : 'opacity-100'}`} data-cy="filter-panel">
                                <FilterPanel />
                            </div>
                            {savedFilterList.length > 0 && <SavedFilterPanel />}
                            <div className="lg:block hidden px-[16px] py-[12px] bg-white">
                                <AISearchPanel />
                            </div>
                        </div>

                        <div className={`px-[16px] py-[12px] bg-white lg:rounded-b-none ${hasScrolled ? 'rounded-b-[10px]' : 'rounded-b-[10px]'}`}>
                            <ExportPanel isGridView={true} onViewChange={() => {}} />
                        </div>
                    </>
                )}

                {/* Show filter panel for non-logged-in users */}
                {!isLoggedIn && (
                    <>
                        {/* Show title and subtitle for non-logged-in users */}
                        <div className="bg-white rounded-t-[15px] px-[16px] py-[20px]">
                            <div className="text-center">
                                <h2 className="text-2xl font-semibold text-my-blue mb-2">
                                    {isFetching ? 'Loading...' : `Discover ${formatNumber(total)} Listings. Sign up to see who they are from.`}
                                </h2>
                                <p className="text-gray-600">
                                    Find properties that are over 2 weeks old. Sign up to access the largest and fully up-to-date database of{' '}
                                    {isFetching ? 'listings' : `${formatNumber(completeTotal)}+ listings`} in Mallorca.
                                </p>
                            </div>
                        </div>

                        <div
                            className={`
                                sticky top-0 bg-white z-20
                                rounded-t-[10px] lg:rounded-b-none ${hasScrolled ? 'rounded-b-[10px]' : 'rounded-b-none'}
                                transition-opacity duration-200
                                ${showFilter ? 'opacity-100' : 'opacity-0 pointer-events-none'}
                            `}
                            style={{
                                willChange: 'opacity',
                                transform: 'translateZ(0)', // Hardware acceleration
                            }}
                        >
                            <div
                                className={`px-[16px] py-[20px] ${showLoading ? 'pointer-events-none opacity-50' : 'opacity-100'}`}
                                data-cy="public-filter-panel"
                            >
                                <PublicFilterPanel />
                            </div>
                            <div className="px-[16px] py-[12px] bg-white lg:block hidden">
                                <div className="flex flex-col gap-[12px] w-full">
                                    <p className="text-base text-my-black lg:block hidden font-medium">
                                        With our AI search you can use filters + specific search queries that look through listings, their descriptions, and
                                        pictures soon too. Sign up to a plan to use it.
                                    </p>
                                    <div className="flex flex-col md:flex-row gap-[12px] w-full">
                                        <div className="h-[48px] w-full">
                                            <div className="h-[48px] w-full rounded-[8px] border border-my-gray border-opacity-80 bg-gray-100">
                                                <div className="flex justify-between items-center gap-[5px] h-full px-[10px]">
                                                    <div className="w-5 h-5 text-gray-400">
                                                        <svg fill="currentColor" viewBox="0 0 20 20">
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        placeholder="Seaview, Terrace, Pool, Parking, etc."
                                                        className="flex-grow bg-transparent text-gray-500"
                                                        disabled
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <button className="min-w-[200px] cursor-not-allowed opacity-50" disabled>
                                            <div className="relative flex">
                                                <div
                                                    className="absolute top-0 left-0 rounded-[5px] w-full h-full z-0"
                                                    style={{
                                                        backgroundColor: '#3176BC',
                                                    }}
                                                ></div>
                                                <div
                                                    className="flex items-center justify-center w-full gap-[5px] rounded-[5px] text-white text-[12px] m-[4px] z-[1] px-[20px] py-[5px]"
                                                    style={{
                                                        backgroundColor: '#3176BC',
                                                    }}
                                                >
                                                    <span className="truncate py-[5px]">AI supported search</span>
                                                </div>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <div className={`pb-[20px] relative ${showLoading ? 'min-h-[400px]' : ''}`}>
                    {showLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                            <PropertiesLoadingAnimation />
                        </div>
                    )}

                    <div className={showLoading ? 'opacity-50' : 'opacity-100'}>
                        <OverviewPropertyTable
                            propertyList={properties}
                            onSort={handleSort}
                            isGridView={true}
                            isForCompetitor={false}
                            shouldShowDetail={true}
                        />

                        {total! > 50 && properties.length > 1 && (
                            <div
                                className="w-full bg-white py-[10px] rounded-t-[10px] lg:rounded-t-none rounded-b-[10px] flex items-center justify-center"
                                data-cy="property-pagination"
                            >
                                <PropertyPagination handlePageChange={handlePageChange} totalPageCount={totalPageCount} currentPage={currentPage} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OverViewContent;
